import { useState, useRef, useCallback, useEffect } from "react";
import { Download, Share2, X, Sparkles, Trophy } from "lucide-react";
import { Button } from "./Button";
import { toast } from "sonner";
import QRCode from "qrcode";

const APP_URL = window.location.origin;

// Streak celebration messages based on milestone
const STREAK_MESSAGES = {
  7: "A week of showing up",
  14: "Two weeks of dedication",
  21: "Three weeks of transformation",
  30: "One month of presence",
  60: "Two months of commitment",
  90: "A quarter of consciousness",
  100: "A hundred days of growth",
  365: "A full year of presence"
};

// Gradient backgrounds for different themes
const THEME_GRADIENTS = {
  presence: ["#2D4A2A", "#1A2D18"],
  confidence: ["#1E3A5F", "#0F1F33"],
  flow: ["#4A3060", "#2A1A38"],
  peace: ["#2A4858", "#152830"],
  courage: ["#5C3D2E", "#2F1F17"]
};

/**
 * Share Streak Card
 * Generates a shareable image card celebrating a streak milestone
 */
export const ShareStreakCard = ({ 
  streak,
  streakTheme,
  intention,
  onClose,
  isVisible = false 
}) => {
  const canvasRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [logoImage, setLogoImage] = useState(null);

  const gradientColors = THEME_GRADIENTS[intention] || THEME_GRADIENTS.presence;
  const milestoneMessage = STREAK_MESSAGES[streak] || `${streak} days of ${streakTheme}`;

  // Load the Reclaim logo
  useEffect(() => {
    if (isVisible && !logoImage) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => setLogoImage(img);
      img.onerror = () => console.log("Logo load error");
      img.src = "/assets/reclaim-logo-white.png";
    }
  }, [isVisible, logoImage]);

  // Generate the card image on canvas
  const generateCard = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const ctx = canvas.getContext('2d');
    const width = 1080;
    const height = 1350; // Square-ish for social media
    
    canvas.width = width;
    canvas.height = height;

    // Draw gradient background
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, gradientColors[0]);
    gradient.addColorStop(1, gradientColors[1]);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Add radial glow in center
    const glow = ctx.createRadialGradient(width/2, height/2 - 100, 0, width/2, height/2 - 100, 400);
    glow.addColorStop(0, 'rgba(255, 255, 255, 0.08)');
    glow.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, width, height);

    // Add subtle grain texture
    ctx.globalAlpha = 0.03;
    for (let i = 0; i < 5000; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? '#FFFFFF' : '#000000';
      ctx.fillRect(Math.random() * width, Math.random() * height, 1, 1);
    }
    ctx.globalAlpha = 1;

    // Draw logo
    if (logoImage) {
      const logoHeight = 45;
      const logoWidth = (logoImage.width / logoImage.height) * logoHeight;
      const logoX = (width - logoWidth) / 2;
      const logoY = 80;
      ctx.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);
    } else {
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '600 48px "Playfair Display", Georgia, serif';
      ctx.textAlign = 'center';
      ctx.fillText('RECLAIM', width / 2, 120);
    }

    // Daily Ritual badge
    ctx.fillStyle = 'rgba(212, 163, 115, 0.9)';
    ctx.font = '500 20px "Inter", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('DAILY RITUAL', width / 2, 180);

    // Trophy/Sparkle icon area (drawn as a circle)
    const iconY = 320;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.beginPath();
    ctx.arc(width / 2, iconY, 80, 0, Math.PI * 2);
    ctx.fill();
    
    // Inner glow
    ctx.fillStyle = 'rgba(212, 163, 115, 0.3)';
    ctx.beginPath();
    ctx.arc(width / 2, iconY, 60, 0, Math.PI * 2);
    ctx.fill();

    // Trophy symbol (using text as placeholder)
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '80px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('🏆', width / 2, iconY + 30);

    // Main streak number
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '700 180px "Playfair Display", Georgia, serif';
    ctx.textAlign = 'center';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 20;
    ctx.fillText(streak.toString(), width / 2, 580);
    ctx.shadowBlur = 0;

    // "Days" label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.font = '500 40px "Inter", sans-serif';
    ctx.fillText('DAYS', width / 2, 650);

    // Theme name
    ctx.fillStyle = 'rgba(212, 163, 115, 1)';
    ctx.font = 'italic 48px "Playfair Display", Georgia, serif';
    ctx.fillText(`of ${streakTheme}`, width / 2, 730);

    // Divider line
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(width / 2 - 150, 800);
    ctx.lineTo(width / 2 + 150, 800);
    ctx.stroke();

    // Celebration message
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '400 32px "Inter", sans-serif';
    ctx.fillText(milestoneMessage, width / 2, 870);

    // Quote box
    const boxWidth = width - 160;
    const boxX = (width - boxWidth) / 2;
    const boxY = 930;
    
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxWidth, 120, 20);
    ctx.fill();

    // Inspirational quote
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.font = 'italic 28px "Playfair Display", Georgia, serif';
    ctx.fillText('"I showed up for myself, every day."', width / 2, boxY + 70);

    // QR Code Section
    const qrY = 1100;
    try {
      const qrDataUrl = await QRCode.toDataURL(APP_URL, {
        width: 120,
        margin: 0,
        color: {
          dark: '#FFFFFF',
          light: '#00000000'
        },
        errorCorrectionLevel: 'M'
      });
      
      const qrImage = new Image();
      await new Promise((resolve, reject) => {
        qrImage.onload = resolve;
        qrImage.onerror = reject;
        qrImage.src = qrDataUrl;
      });
      
      const qrSize = 100;
      ctx.drawImage(qrImage, (width - qrSize) / 2, qrY, qrSize, qrSize);
    } catch (err) {
      console.log("QR code generation error:", err);
    }

    // Footer
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '400 20px "Inter", sans-serif';
    ctx.fillText('Start your journey at reclaim.app', width / 2, qrY + 140);

    return canvas.toDataURL('image/png');
  }, [streak, streakTheme, gradientColors, milestoneMessage, logoImage]);

  // Update preview when options change
  const updatePreview = useCallback(async () => {
    setIsGenerating(true);
    const url = await generateCard();
    setPreviewUrl(url);
    setIsGenerating(false);
  }, [generateCard]);

  // Generate preview on mount
  useEffect(() => {
    if (isVisible) {
      updatePreview();
    }
  }, [isVisible, updatePreview]);

  // Download image
  const handleDownload = async () => {
    setIsGenerating(true);
    const dataUrl = await generateCard();
    
    const link = document.createElement('a');
    link.download = `${streak}-days-of-${streakTheme.toLowerCase()}.png`;
    link.href = dataUrl;
    link.click();
    
    toast.success("Image saved!", { duration: 3000 });
    setIsGenerating(false);
  };

  // Share via native share sheet
  const handleShare = async () => {
    setIsGenerating(true);
    
    try {
      const dataUrl = await generateCard();
      
      if (!dataUrl) {
        toast.error("Failed to generate share image");
        setIsGenerating(false);
        return;
      }
      
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], `${streak}-days-of-${streakTheme.toLowerCase()}.png`, { type: 'image/png' });

      const canShareFiles = navigator.share && navigator.canShare && navigator.canShare({ files: [file] });
      
      if (canShareFiles) {
        try {
          await navigator.share({
            files: [file],
            title: `${streak} Days of ${streakTheme}`,
            text: `I completed ${streak} days of ${streakTheme} with Reclaim! 🏆`
          });
          setIsGenerating(false);
          return;
        } catch (err) {
          if (err.name === 'AbortError') {
            setIsGenerating(false);
            return;
          }
        }
      }
      
      // Fallback: Download
      const link = document.createElement('a');
      link.download = `${streak}-days-of-${streakTheme.toLowerCase()}.png`;
      link.href = dataUrl;
      link.click();
      
      toast.success("Image saved! Share it manually.", { duration: 4000 });
      
    } catch (err) {
      console.error("Share error:", err);
      toast.error("Unable to share. Please try again.");
    }
    
    setIsGenerating(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] animate-fade-in overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4 pb-24">
        <div className="bg-white rounded-3xl max-w-sm w-full shadow-2xl animate-fade-in-up">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-2">
              <Trophy className="text-[#D4A373]" size={20} />
              <h3 className="font-heading text-lg text-[#1F2937]">Streak Milestone!</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#F3F4F6] rounded-full transition-colors"
              data-testid="close-streak-card-modal"
            >
              <X size={20} className="text-[#6B7280]" />
            </button>
          </div>

          {/* Preview */}
          <div className="p-4 space-y-4">
            <div className="relative aspect-[4/5] bg-gradient-to-b from-[#2D4A2A] to-[#1A2D18] rounded-2xl overflow-hidden shadow-inner">
              {previewUrl ? (
                <img 
                  src={previewUrl} 
                  alt="Streak card preview" 
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Celebration text */}
            <div className="text-center py-2">
              <p className="text-[#1F2937] font-heading text-xl">
                {streak} Days of {streakTheme}!
              </p>
              <p className="text-[#6B7280] text-sm mt-1">
                Share your achievement and inspire others
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleDownload}
                disabled={isGenerating}
                className="flex-1"
                data-testid="download-streak-card-btn"
              >
                <Download size={18} />
                Save
              </Button>
              <Button
                onClick={handleShare}
                disabled={isGenerating}
                className="flex-1"
                data-testid="share-streak-card-btn"
              >
                <Share2 size={18} />
                Share
              </Button>
            </div>
          </div>

          {/* Hidden canvas for image generation */}
          <canvas ref={canvasRef} className="hidden" />
        </div>
      </div>
    </div>
  );
};

export default ShareStreakCard;
